import React, {
  ChangeEvent, useCallback, useEffect, useState,
} from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { MdOutlineSearch, MdOutlineClear } from 'react-icons/md';
import { paletteColor } from 'common/theme/NutailTheme';

type SimpleSearchProps = {
    search: string;
    setSearch: (search: string) => void;
    fullWidth?: boolean;
    debounceTimeInMs?: number;
    placeHolder?: string;
};

export function SimpleSearch({
  search = '',
  setSearch,
  fullWidth = false,
  debounceTimeInMs = 500,
  placeHolder = 'Search',
}: SimpleSearchProps): React.JSX.Element {
  const [internalSearch, setInternalSearch] = useState(search);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setInternalSearch(e?.target?.value ?? ''),
    [],
  );

  useEffect(() => {
    setInternalSearch(search);
  }, [search]);

  useEffect(() => {
    if (internalSearch === search) {
      return;
    }

    const timerId = setTimeout(() => {
      setSearch(internalSearch);
    }, debounceTimeInMs);

    // eslint-disable-next-line consistent-return
    return (): void => {
      clearTimeout(timerId);
    };
  }, [internalSearch, setSearch, search, debounceTimeInMs]);

  return (
    <TextField
      fullWidth={fullWidth}
      sx={{ backgroundColor: paletteColor('background.paper') }}
      size="small"
      value={internalSearch}
      onChange={handleChange}
      placeholder={placeHolder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <MdOutlineSearch />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {!!search && (
            <IconButton
              aria-label="clear"
              onClick={() => setSearch('')}
            >
              <MdOutlineClear />
            </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}
